import { useAtom } from '@effect/atom-react';
import { Duration, Effect, Schema } from 'effect';
import { AsyncResult, Atom } from 'effect/reactivity';
import { useId } from 'react';

export class ClipboardCopyError extends Schema.TaggedError<ClipboardCopyError>()(
  'ClipboardCopyError',
  {},
) {}

const COPIED_FEEDBACK = Duration.millis(1500);

const selectedTextArea = (text: string) =>
  Effect.acquireRelease(
    Effect.sync(() => {
      const focused = document.activeElement;
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.readOnly = true;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      const host =
        focused === null || focused === document.body
          ? document.body
          : (focused.closest('[role="dialog"], [role="alertdialog"]') ??
            focused.parentElement ??
            document.body);
      host.append(textArea);
      textArea.select();
      textArea.setSelectionRange(0, text.length);
      return { textArea, focused };
    }),
    ({ textArea, focused }) =>
      Effect.sync(() => {
        textArea.remove();
        if (focused instanceof HTMLElement) focused.focus();
      }),
  );

const copyWithSelection = (text: string) =>
  selectedTextArea(text).pipe(
    Effect.andThen(
      Effect.try({
        // oxlint-disable-next-line typescript/no-deprecated -- the Clipboard API rejects in in-app browsers and when permission is denied; execCommand still copies there
        try: () => document.execCommand('copy'),
        catch: () => ClipboardCopyError.make({}),
      }),
    ),
    Effect.filterOrFail(
      (copied) => copied,
      () => ClipboardCopyError.make({}),
    ),
    Effect.scoped,
  );

export const copyText = (text: string) =>
  Effect.tryPromise(() => navigator.clipboard.writeText(text)).pipe(
    Effect.catchTag('UnknownError', () => copyWithSelection(text)),
  );

const copyTextAtom = Atom.family((_copier: string) =>
  Atom.fn((text: string, get) =>
    copyText(text).pipe(
      Effect.tap(() =>
        Effect.sync(() => get.setSelf(AsyncResult.success(true))),
      ),
      Effect.andThen(Effect.sleep(COPIED_FEEDBACK)),
      Effect.as(false),
    ),
  ),
);

export function useCopyText() {
  const [result, copy] = useAtom(copyTextAtom(useId()), {
    mode: 'promiseExit',
  });
  return {
    copy,
    copying: result.waiting,
    copied: AsyncResult.getOrElse(result, () => false),
    failed: AsyncResult.isFailure(result) && !result.waiting,
  };
}
